import Block from '@shared/components/ui/Block';
import { useControlField, ValidatedForm } from 'remix-validated-form';
import styled from 'styled-components';
import { RadioGroup } from './RadioGroup';
import { OutlineRadioInput } from './OutlineRadioInput';
import { Breakpoints } from '@shared/styles/breakpoints';
import { Input, StyledLabel } from './Input';
import { Text } from '../Text';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useOptionalUser } from '@/hooks/useOptionalUser';
import invariant from 'tiny-invariant';
import DefaqtoIcon from '@shared/components/icons/Defaqto';
import DynamicPlannerIcon from '@shared/components/icons/DynamicPlanner';
import EValueOneToFiveIcon from '@shared/components/icons/EValueOneToFive';
import EValueOneToTenIcon from '@shared/components/icons/EValueOneToTen';
import OxfordRiskIcon from '@shared/components/icons/OxfordRisk';
import { RiskProfilers } from '@shared/data/RiskProfilers';
import { Button } from '../Button';
import { useCallback, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { AnimateContentPresenceWrapper } from '../AnimateContentPresence';
import type { Client } from '@/api/clients';
import { saveClient, updateClient } from '@/api/clients';
import { withZod } from '@remix-validated-form/with-zod';
import { zfd } from 'zod-form-data';
import { z } from 'zod';
import { Notice } from '@shared/components/ui/Notice';
import { Card } from '../Card';

export const clientValidator = withZod(
    z
        .object({
            name: zfd.text(z.string().min(3, 'Must be at least 3 characters')),
            riskLevel: zfd.numeric(
                z
                    .number({ message: 'Must be a number' })
                    .min(1, 'Must be at least 1')
                    .max(10, 'Must be at most 10')
                    .nullable()
                    .optional()
            ),
            investmentTermInYears: zfd.numeric(
                z
                    .number({ message: 'Must be a number' })
                    .refine(
                        (value) => [5, 10, 15, 20, 25].includes(value),
                        'Must be one of 5, 10, 15, 20, or 25'
                    )
                    .optional()
            ),
            riskProfiler: zfd.text(
                z
                    .enum(Object.values(RiskProfilers) as [string, ...string[]])
                    .optional()
            ),
        })
        .refine(
            (data) => {
                return !(
                    data.riskLevel !== undefined &&
                    data.investmentTermInYears === undefined
                );
            },
            {
                message: 'Investment term is required if risk level is set',
                path: ['investmentTermInYears'],
            }
        )
);

type Props = {
    client?: Client;
};

type ClientFormData = z.infer<typeof clientValidator.schema>;

export function ClientForm({ client }: Props) {
    const user = useOptionalUser();
    invariant(user, 'User must be logged in to create a client');
    const navigate = useNavigate();
    const isInEditMode = !!client;

    // Was a Remix action (create/edit routes); now a mutation that navigates
    // to the client on success.
    const mutation = useMutation({
        mutationFn: (data: ClientFormData) => {
            const riskProfiler = user.organisation.riskProfiler ?? undefined;
            if (isInEditMode && client) {
                return updateClient(client.clientId, {
                    name: data.name,
                    riskLevel: data.riskLevel ?? null,
                    investmentTermInYears: data.investmentTermInYears ?? null,
                    ...(riskProfiler ? { riskProfiler } : {}),
                });
            }
            return saveClient({
                name: data.name,
                ...(data.riskLevel != null ? { riskLevel: data.riskLevel } : {}),
                ...(data.investmentTermInYears != null
                    ? { investmentTermInYears: data.investmentTermInYears }
                    : {}),
                ...(riskProfiler ? { riskProfiler } : {}),
            });
        },
        onSuccess: (res) => {
            if (res.data) navigate(`/client/${res.data.clientId}`);
        },
    });

    const errorMessage =
        mutation.data?.error ??
        (mutation.isError ? 'Something went wrong' : null);

    return (
        <ValidatedForm
            id="create"
            validator={clientValidator}
            defaultValues={{
                name: client?.name ?? '',
                riskLevel: client?.riskLevel ?? undefined,
                investmentTermInYears:
                    client?.investmentTermInYears ?? undefined,
            }}
            onSubmit={(data) => mutation.mutate(data as ClientFormData)}
        >
            <ClientFormFields
                user={user}
                client={client}
                isInEditMode={isInEditMode}
                isSubmitting={mutation.isPending}
                errorMessage={errorMessage}
            />
        </ValidatedForm>
    );
}

type FieldsProps = {
    user: NonNullable<ReturnType<typeof useOptionalUser>>;
    client?: Client;
    isInEditMode: boolean;
    isSubmitting: boolean;
    errorMessage: string | null;
};

// Inner component: lives inside <ValidatedForm> so the form-field hooks
// (useControlField, etc.) have the react-hook-form provider in scope.
function ClientFormFields({
    user,
    client,
    isInEditMode,
    isSubmitting,
    errorMessage,
}: FieldsProps) {
    const isEditingAndHasDifferentRiskProfilerLevels =
        isInEditMode &&
        user.organisation.riskProfiler !== null &&
        client?.riskProfiler != null &&
        user.organisation.riskProfiler !== client.riskProfiler;
    const [riskLevel, setRiskLevel] = useState<number | undefined>(
        client?.riskLevel ?? undefined
    );
    const [isRiskInputsDisabled, setIsRiskInputsDisabled] = useState(
        isEditingAndHasDifferentRiskProfilerLevels ? true : false
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setInvestmentTermInYears] = useControlField<number | undefined>(
        'investmentTermInYears'
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [__, setRiskLevelControl] = useControlField<number | undefined>(
        'riskLevel'
    );

    const handleRiskLevelChange = useCallback(
        (value: number | string) => {
            setRiskLevel(value === '' ? undefined : Number(value));

            if (value === '') {
                setInvestmentTermInYears(undefined);
            }
        },
        [setInvestmentTermInYears]
    );

    const handleUpdateToNewRiskProfiler = useCallback(() => {
        setIsRiskInputsDisabled(false);
        setRiskLevelControl(undefined);
        setInvestmentTermInYears(undefined);
    }, [setInvestmentTermInYears, setRiskLevelControl]);

    return (
        <>
            <FormLayout spacing="xLarge">
                <Input
                    label="Client name"
                    name="name"
                    placeholder="Client name"
                />
            </FormLayout>

            {user.organisation.riskProfiler === null && (
                <Card spacing="xLarge">
                    <RiskProfilerIcons spacing="large">
                        <RiskProfilerIconWrapper>
                            <DefaqtoIcon mode="dark" />
                        </RiskProfilerIconWrapper>

                        <RiskProfilerIconWrapper>
                            <DynamicPlannerIcon mode="dark" />
                        </RiskProfilerIconWrapper>

                        <RiskProfilerIconWrapper>
                            <EValueOneToFiveIcon mode="dark" />
                        </RiskProfilerIconWrapper>

                        <RiskProfilerIconWrapper>
                            <EValueOneToTenIcon mode="dark" />
                        </RiskProfilerIconWrapper>

                        <RiskProfilerIconWrapper>
                            <OxfordRiskIcon mode="dark" />
                        </RiskProfilerIconWrapper>
                    </RiskProfilerIcons>
                    <Text align="center" size="small" color="uNavy">
                        (optional) Select which risk profiler you use in{' '}
                        <Link
                            to={`/settings/organisation`}
                            style={{ color: 'inherit' }}
                        >
                            organisation settings
                        </Link>{' '}
                        to enable client risk appetite
                    </Text>
                </Card>
            )}

            {isRiskInputsDisabled && (
                <Notice
                    $backgroundColor="yellow"
                    $fillColor="dark"
                    $textColor="dark"
                    $strokeColor="yellow"
                    spacing="large"
                >
                    Warning: This client's risk appetite was set with a
                    different profiler, would you like set a new risk level?
                    <Button
                        $variant="slim"
                        style={{ marginLeft: 20 }}
                        onClick={handleUpdateToNewRiskProfiler}
                    >
                        Set new risk level
                    </Button>
                </Notice>
            )}

            {user.organisation.riskProfiler !== null && (
                <>
                    <input
                        type="hidden"
                        name="riskProfiler"
                        value={user.organisation.riskProfiler}
                    />

                    <Block spacing="xLarge">
                        <Block>
                            <Text spacing="xSmall">Risk level (optional)</Text>
                        </Block>

                        <Block
                            $stack="horizontal"
                            $gap={12}
                            spacing="small"
                            $disabled={isRiskInputsDisabled}
                        >
                            <WhiteRiskProfilerIconWrapper>
                                {(isRiskInputsDisabled
                                    ? client?.riskProfiler
                                    : user.organisation.riskProfiler) ===
                                    RiskProfilers.DEFAQTO && (
                                    <DefaqtoIcon mode="dark" />
                                )}

                                {(isRiskInputsDisabled
                                    ? client?.riskProfiler
                                    : user.organisation.riskProfiler) ===
                                    RiskProfilers.DYNAMIC_PLANNER && (
                                    <DynamicPlannerIcon mode="dark" />
                                )}

                                {(isRiskInputsDisabled
                                    ? client?.riskProfiler
                                    : user.organisation.riskProfiler) ===
                                    RiskProfilers.EVALUE_ONE_TO_FIVE && (
                                    <EValueOneToFiveIcon mode="dark" />
                                )}

                                {(isRiskInputsDisabled
                                    ? client?.riskProfiler
                                    : user.organisation.riskProfiler) ===
                                    RiskProfilers.EVALUE_ONE_TO_TEN && (
                                    <EValueOneToTenIcon mode="dark" />
                                )}

                                {(isRiskInputsDisabled
                                    ? client?.riskProfiler
                                    : user.organisation.riskProfiler) ===
                                    RiskProfilers.OXFORD_RISK && (
                                    <OxfordRiskIcon mode="dark" />
                                )}
                            </WhiteRiskProfilerIconWrapper>

                            <RiskLevelRadioGroup name="riskLevel">
                                {(user.organisation.riskProfiler ===
                                    RiskProfilers.EVALUE_ONE_TO_FIVE ||
                                user.organisation.riskProfiler ===
                                    RiskProfilers.OXFORD_RISK
                                    ? [
                                          { value: '', label: 'None' },
                                          { value: 1, label: '1' },
                                          { value: 2, label: '2' },
                                          { value: 3, label: '3' },
                                          { value: 4, label: '4' },
                                          { value: 5, label: '5' },
                                      ]
                                    : [
                                          { value: '', label: 'None' },
                                          { value: 1, label: '1' },
                                          { value: 2, label: '2' },
                                          { value: 3, label: '3' },
                                          { value: 4, label: '4' },
                                          { value: 5, label: '5' },
                                          { value: 6, label: '6' },
                                          { value: 7, label: '7' },
                                          { value: 8, label: '8' },
                                          { value: 9, label: '9' },
                                          { value: 10, label: '10' },
                                      ]
                                ).map(({ label, value }) => (
                                    <OutlineRadioInput
                                        key={`${user.organisation.riskProfiler}-${value}`}
                                        value={value}
                                        label={label}
                                        name="riskLevel"
                                        handleChange={() =>
                                            handleRiskLevelChange(value)
                                        }
                                    />
                                ))}
                            </RiskLevelRadioGroup>
                        </Block>

                        <Text size="small" color="uNavy">
                            You can change your default risk profiler in{' '}
                            <Link
                                to="/settings/organisation"
                                style={{ color: 'inherit' }}
                            >
                                organisation settings
                            </Link>
                            .
                        </Text>
                    </Block>

                    <AnimatePresence>
                        {riskLevel && (
                            <AnimateContentPresenceWrapper>
                                <Block spacing="xLarge">
                                    <Block>
                                        <Text spacing="xSmall" color="uNavy">
                                            Investment term
                                        </Text>
                                    </Block>

                                    <Block
                                        $stack="horizontal"
                                        $gap={12}
                                        spacing="small"
                                        $disabled={isRiskInputsDisabled}
                                    >
                                        <InvestmentTermRadioGroup name="investmentTermInYears">
                                            {[
                                                { value: 5, label: '5 years' },
                                                {
                                                    value: 10,
                                                    label: '10 years',
                                                },
                                                {
                                                    value: 15,
                                                    label: '15 years',
                                                },
                                                {
                                                    value: 20,
                                                    label: '20 years',
                                                },
                                                {
                                                    value: 25,
                                                    label: '25 years',
                                                },
                                            ].map(({ value, label }) => (
                                                <OutlineRadioInput
                                                    key={value}
                                                    value={value}
                                                    label={label}
                                                    name="investmentTermInYears"
                                                />
                                            ))}
                                        </InvestmentTermRadioGroup>
                                    </Block>
                                </Block>
                            </AnimateContentPresenceWrapper>
                        )}
                    </AnimatePresence>
                </>
            )}

            <Block $align="center" spacing="medium">
                <Button type="submit" $variant="icon" disabled={isSubmitting}>
                    {isInEditMode ? 'Update' : 'Create'}
                </Button>
            </Block>

            {errorMessage && (
                <Error spacing="medium" size="xSmall">
                    {errorMessage}
                </Error>
            )}
        </>
    );
}

const FormLayout = styled(Block)`
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: 12px;

    ${StyledLabel} {
        width: 100%;
    }

    @media (min-width: ${Breakpoints.Small}) {
        flex-direction: row;
    }
`;

const Error = styled(Text)`
    border-radius: 8px;
    background-color: ${({ theme }) => theme.colors.red};
    padding: 8px 12px;
    display: inline-block;
    margin-top: 12px;
`;

const RiskLevelRadioGroup = styled(RadioGroup)`
    fieldset {
        gap: 12px;
    }

    ${OutlineRadioInput} {
        width: 48px;
        height: 48px;
    }
`;

const InvestmentTermRadioGroup = styled(RadioGroup)`
    ${OutlineRadioInput} {
        width: 130px;
        height: 48px;
    }
`;

const RiskProfilerIcons = styled(Block)`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
`;

const RiskProfilerIconWrapper = styled.div`
    padding: 8px;
    border-radius: 8px;
    border: solid 1px ${({ theme }) => theme.colors.uNavy10};
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 100px;
        height: 32px;
    }
`;

const WhiteRiskProfilerIconWrapper = styled(RiskProfilerIconWrapper)`
    background-color: ${({ theme }) => theme.colors.white};
`;
